/**
 * components/Shell.tsx
 * ------------------------------------------------------------------
 * Authenticated app shell.
 *   - Solid Acepsle-orange top nav (sticky), brand colour leading.
 *   - A plain-orange header band beneath it showing the active
 *     module's title (no banner image — plain orange reads better).
 *   - The right side carries a single account icon; clicking it
 *     reveals a small menu with the owner's name/role, a dark-mode
 *     toggle, and sign out. The email is no longer shown inline.
 * ------------------------------------------------------------------
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import {
  LayoutDashboard,
  Users,
  FileText,
  ScrollText,
  ShieldCheck,
  Sparkles,
  Receipt,
  FileSignature,
  RefreshCw,
  FileMinus,
  Wallet,
  Mail,
  Settings as SettingsIcon,
  Image as ImageIcon,
  UserCog,
  LogOut,
  Menu,
  X,
  User,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import AcepsleWordmark from '@/components/branding/AcepsleWordmark';
import ThemeToggle from '@/components/ThemeToggle';
import PWAPrompt from '@/components/PWAPrompt';
import { CompanySettings } from '@/features/settings/CompanySettings';
import { CommsPage } from '@/features/comms/CommsPage';
import { ClientsManager } from '@/features/clients/ClientsManager';
import { InvoicesManager } from '@/features/invoices/InvoicesManager';
import { PaymentsManager } from '@/features/payments/PaymentsManager';
import { QuotesManager } from '@/features/quotes/QuotesManager';
import { LettersManager } from '@/features/letters/LettersManager';
import { ExpensesManager } from '@/features/expenses/ExpensesManager';
import { Dashboard } from '@/features/dashboard/Dashboard';
import { LeadsManager } from '@/features/leads/LeadsManager';
import { ComplianceRegister } from '@/features/compliance/ComplianceRegister';
import { UserManagement } from '@/features/admin/UserManagement';
import { MediaManager } from '@/features/media/MediaManager';
import { CreditNotesManager } from '@/features/credit/CreditNotesManager';
import { RecurringManager } from '@/features/recurring/RecurringManager';
import { StatementsView } from '@/features/statements/StatementsView';
import { canAccess } from '@/lib/permissions';

export type NavKey =
  | 'dashboard'
  | 'comms'
  | 'clients'
  | 'invoices'
  | 'payments'
  | 'quotes'
  | 'recurring'
  | 'credit'
  | 'statements'
  | 'letters'
  | 'compliance'
  | 'leads'
  | 'expenses'
  | 'settings'
  | 'media'
  | 'users';

interface NavItem {
  key: NavKey;
  label: string;
  icon: ReactNode;
  phase: number;
  blurb: string;
}

const NAV: NavItem[] = [
  { key: 'dashboard', label: 'Dashboard', icon: <LayoutDashboard size={20} />, phase: 4, blurb: 'Where Acepsle stands today.' },
  { key: 'comms', label: 'Communications', icon: <Mail size={20} />, phase: 1, blurb: 'Branded email and text messages for schools, with delivery tracking.' },
  { key: 'clients', label: 'Clients', icon: <Users size={20} />, phase: 2, blurb: 'Your customers and their details.' },
  { key: 'invoices', label: 'Invoices', icon: <FileText size={20} />, phase: 2, blurb: 'Raise and track invoices.' },
  { key: 'payments', label: 'Payments', icon: <Receipt size={20} />, phase: 2, blurb: 'Record and reconcile payments.' },
  { key: 'quotes', label: 'Quotes', icon: <FileSignature size={20} />, phase: 3, blurb: 'Estimates that convert to invoices.' },
  { key: 'recurring', label: 'Recurring', icon: <RefreshCw size={20} />, phase: 3, blurb: 'Automate repeating invoices.' },
  { key: 'credit', label: 'Credit Notes', icon: <FileMinus size={20} />, phase: 3, blurb: 'Refunds and adjustments.' },
  { key: 'statements', label: 'Statements', icon: <FileText size={20} />, phase: 3, blurb: 'Client account statements with running balance.' },
  { key: 'letters', label: 'Documents', icon: <ScrollText size={20} />, phase: 4, blurb: 'Company letters, resolutions & confirmations.' },
  { key: 'compliance', label: 'Compliance', icon: <ShieldCheck size={20} />, phase: 6, blurb: 'Statutory documents and renewal dates.' },
  { key: 'leads', label: 'Leads', icon: <Sparkles size={20} />, phase: 6, blurb: 'Website enquiries and your sales pipeline.' },
  { key: 'expenses', label: 'Expenses', icon: <Wallet size={20} />, phase: 4, blurb: 'Track what the business spends.' },
  { key: 'settings', label: 'Settings', icon: <SettingsIcon size={20} />, phase: 0, blurb: 'Your business details, branding, and VAT status.' },
  { key: 'media', label: 'Website images', icon: <ImageIcon size={20} />, phase: 0, blurb: 'Screenshots shown on the public company page.' },
  { key: 'users', label: 'Users', icon: <UserCog size={20} />, phase: 0, blurb: 'Manage who can access the hub and their roles.' },
];

const TITLES: Record<NavKey, string> = {
  dashboard: 'Dashboard',
  comms: 'Communications',
  clients: 'Clients',
  invoices: 'Invoices',
  payments: 'Payments',
  quotes: 'Quotes',
  recurring: 'Recurring invoices',
  credit: 'Credit notes',
  statements: 'Statements',
  letters: 'Company documents',
  compliance: 'Compliance register',
  leads: 'Leads',
  expenses: 'Expenses',
  settings: 'Company settings',
  media: 'Website images',
  users: 'User management',
};

function Placeholder({ item }: { item: NavItem }) {
  return (
    <div className="mx-auto max-w-xl py-16 text-center">
      <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-brandSoft text-brand">
        {item.icon}
      </div>
      <h2 className="text-xl font-semibold text-primary dark:text-gray-100">
        {item.label}
      </h2>
      <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
        This module ships in Phase {item.phase}. The foundation it sits on —
        data model, numbering, VAT handling, and auth — is already in place.
      </p>
    </div>
  );
}

function AccountMenu() {
  const { user, role, signOut } = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onDoc(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        aria-label="Account"
        className="flex h-11 w-11 items-center justify-center rounded-full bg-white/15 text-white transition-all duration-200 hover:scale-105 hover:bg-white/25"
      >
        <User size={22} />
      </button>

      {open && (
        <div className="absolute right-0 z-50 mt-2 w-60 overflow-hidden rounded-card border border-black/5 bg-surface shadow-pop dark:border-white/10 dark:bg-surfaceDark">
          <div className="border-b border-black/5 px-4 py-3 dark:border-white/10">
            <div className="truncate text-sm font-semibold text-primary dark:text-gray-100">
              {user?.email}
            </div>
            <div className="mt-0.5 text-xs capitalize text-primaryLight dark:text-gray-400">
              {role}
            </div>
          </div>

          <div className="flex items-center justify-between px-4 py-2.5">
            <span className="text-sm text-primary dark:text-gray-200">Dark mode</span>
            <ThemeToggle />
          </div>

          <button
            onClick={() => {
              setOpen(false);
              void signOut();
            }}
            className="flex w-full items-center gap-2 border-t border-black/5 px-4 py-2.5 text-left text-sm font-semibold text-primaryLight transition hover:bg-black/5 dark:border-white/10 dark:text-gray-300 dark:hover:bg-white/5"
          >
            <LogOut size={16} /> Sign out
          </button>
        </div>
      )}
    </div>
  );
}

export function Shell() {
  const { role } = useAuth();
  const [active, setActive] = useState<NavKey>('dashboard');
  const [menuOpen, setMenuOpen] = useState(false);

  const visibleNav = NAV.filter((n) => canAccess(role, n.key));

  // If the current role can't access the active tab, fall back to dashboard.
  useEffect(() => {
    if (role && !canAccess(role, active)) setActive('dashboard');
  }, [role, active]);

  const activeItem = NAV.find((n) => n.key === active)!;

  // index.html carries the SEO title for the public company page; once a
  // user is inside the hub, reflect the module they're actually on.
  useEffect(() => {
    document.title = `${TITLES[active]} · Acepsle Hub`;
  }, [active]);

  function NavButton({ item, block = false }: { item: NavItem; block?: boolean }) {
    const isActive = active === item.key;
    const base = 'flex items-center rounded-lg font-semibold transition';
    const palette = isActive
      ? 'bg-white text-brand'
      : 'text-white/90 hover:bg-white/15 hover:text-white';

    // Mobile dropdown keeps icon + label (no hover on touch).
    if (block) {
      return (
        <button
          onClick={() => {
            setActive(item.key);
            setMenuOpen(false);
          }}
          className={`${base} ${palette} w-full gap-2 px-3 py-2 text-sm`}
        >
          {item.icon}
          <span>{item.label}</span>
        </button>
      );
    }

    // Desktop: icon only, full name on hover.
    return (
      <div className="group relative">
        <button
          onClick={() => {
            setActive(item.key);
            setMenuOpen(false);
          }}
          aria-label={item.label}
          title={item.label}
          className={`${base} ${palette} p-3 transition-all duration-200 hover:scale-110 active:scale-95`}
        >
          {item.icon}
        </button>
        <span className="pointer-events-none absolute left-1/2 top-full z-50 mt-2 -translate-x-1/2 translate-y-1 whitespace-nowrap rounded-lg bg-brandSoft px-2.5 py-1 text-xs font-semibold text-brand opacity-0 shadow-pop ring-1 ring-brand/10 transition-all duration-200 group-hover:translate-y-0 group-hover:opacity-100">
          {item.label}
        </span>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col text-ink dark:text-gray-100">
      {/* Solid orange top nav (sticky) */}
      <header className="sticky top-0 z-40 bg-brand shadow-card">
        <div className="mx-auto flex h-20 max-w-7xl items-center gap-4 px-4 sm:px-6">
          <button
            onClick={() => setActive('dashboard')}
            className="shrink-0 rounded-lg transition hover:opacity-90 focus:outline-none"
            aria-label="Go to dashboard"
          >
            <AcepsleWordmark height={24} variant="reversed" />
          </button>

          <nav className="ml-2 hidden flex-1 items-center gap-1 lg:flex">
            {visibleNav.map((item) => (
              <NavButton key={item.key} item={item} />
            ))}
          </nav>

          <div className="ml-auto flex items-center gap-2">
            <AccountMenu />
            <button
              className="rounded-lg p-2 text-white hover:bg-white/15 lg:hidden"
              onClick={() => setMenuOpen((v) => !v)}
              aria-label="Toggle menu"
            >
              {menuOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>

        {menuOpen && (
          <div className="border-t border-white/15 px-4 pb-3 pt-2 lg:hidden">
            <div className="grid grid-cols-2 gap-1">
              {visibleNav.map((item) => (
                <NavButton key={item.key} item={item} block />
              ))}
            </div>
          </div>
        )}
      </header>

      {/* Plain-orange header band — page title; scrolls away under the nav */}
      <div className="flex h-28 items-end bg-brand">
        <div className="mx-auto w-full max-w-7xl px-4 pb-5 sm:px-6">
          <h1 className="text-2xl font-bold text-white sm:text-3xl">
            {TITLES[active]}
          </h1>
          <p className="mt-1 text-sm text-white/85">{activeItem.blurb}</p>
        </div>
      </div>

      {/* Content */}
      <main className="flex-1">
        <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 sm:py-8">
          {active === 'dashboard' ? (
            <Dashboard />
          ) : active === 'users' ? (
            <UserManagement />
          ) : active === 'settings' ? (
            <CompanySettings />
          ) : active === 'media' ? (
            <MediaManager />
          ) : active === 'comms' ? (
            <CommsPage />
          ) : active === 'clients' ? (
            <ClientsManager />
          ) : active === 'invoices' ? (
            <InvoicesManager />
          ) : active === 'payments' ? (
            <PaymentsManager />
          ) : active === 'quotes' ? (
            <QuotesManager />
          ) : active === 'recurring' ? (
            <RecurringManager />
          ) : active === 'credit' ? (
            <CreditNotesManager />
          ) : active === 'statements' ? (
            <StatementsView />
          ) : active === 'letters' ? (
            <LettersManager />
          ) : active === 'compliance' ? (
            <ComplianceRegister />
          ) : active === 'leads' ? (
            <LeadsManager />
          ) : active === 'expenses' ? (
            <ExpensesManager />
          ) : (
            <Placeholder item={activeItem} />
          )}
        </div>
      </main>

      <PWAPrompt />
    </div>
  );
}
